<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ messages: [], buttons_enabled: false, buttons: [] }),
  },
});

const emit = defineEmits(['update:modelValue']);

// Local state
const messages = ref([]);
const buttonsEnabled = ref(false);
const buttons = ref([]);

// Initialize from props
watch(
  () => props.modelValue,
  newVal => {
    if (newVal) {
      messages.value = newVal.messages || [];
      buttonsEnabled.value = newVal.buttons_enabled || false;
      buttons.value = newVal.buttons || [];
    }
  },
  { immediate: true }
);

// Emit changes
const emitUpdate = () => {
  emit('update:modelValue', {
    messages: messages.value,
    buttons_enabled: buttonsEnabled.value,
    buttons: buttons.value,
  });
};

// Message operations
const addMessage = () => {
  if (messages.value.length < 3) {
    messages.value.push({ content: '', delay: 0 });
    emitUpdate();
  }
};

const removeMessage = index => {
  messages.value.splice(index, 1);
  emitUpdate();
};

// Button operations
const addButton = () => {
  if (buttons.value.length < 10) {
    buttons.value.push({ label: '', keyword: '' });
    emitUpdate();
  }
};

const removeButton = index => {
  buttons.value.splice(index, 1);
  emitUpdate();
};

const canAddMessage = computed(() => messages.value.length < 3);
const canAddButton = computed(() => buttons.value.length < 10);
</script>

<template>
  <div class="greeting-editor">
    <!-- Greeting Message -->
    <div class="section">
      <div class="section-header">
        <span class="section-title">Greeting Message</span>
        <button v-if="canAddMessage" class="add-btn" @click="addMessage">
          + Add
        </button>
      </div>

      <div v-if="messages.length === 0" class="empty-hint">
        Click "+ Add" to create greeting message
      </div>

      <div v-for="(msg, index) in messages" :key="index" class="message-item">
        <div class="message-header">
          <span class="message-label">Message {{ index + 1 }}</span>
          <div class="message-actions">
            <input
              v-if="index > 0"
              type="number"
              :value="msg.delay"
              min="0"
              max="30"
              class="delay-input"
              placeholder="Delay"
              @input="
                msg.delay = parseInt($event.target.value) || 0;
                emitUpdate();
              "
            />
            <span v-if="index > 0" class="delay-label">sec delay</span>
            <button class="delete-btn" @click="removeMessage(index)">✕</button>
          </div>
        </div>
        <textarea
          v-model="msg.content"
          rows="2"
          class="message-textarea"
          placeholder="Enter message content..."
          @input="emitUpdate()"
        />
      </div>
    </div>

    <!-- Quick Reply Buttons -->
    <div class="section">
      <div class="section-header">
        <label class="toggle-label">
          <input
            v-model="buttonsEnabled"
            type="checkbox"
            @change="emitUpdate()"
          />
          <span class="toggle-text">Quick Reply Buttons</span>
        </label>
        <button
          v-if="buttonsEnabled && canAddButton"
          class="add-btn"
          @click="addButton"
        >
          + Add
        </button>
      </div>

      <div v-if="buttonsEnabled" class="buttons-section">
        <div v-if="buttons.length === 0" class="empty-hint">
          Click "+ Add" to create button
        </div>

        <div v-for="(btn, index) in buttons" :key="index" class="button-item">
          <input
            v-model="btn.label"
            type="text"
            class="button-input"
            placeholder="Button text"
            @input="emitUpdate()"
          />
          <span class="arrow">→</span>
          <input
            v-model="btn.keyword"
            type="text"
            class="button-input"
            placeholder="Send keyword"
            @input="emitUpdate()"
          />
          <button class="delete-btn" @click="removeButton(index)">✕</button>
        </div>

        <!-- Preview -->
        <div v-if="buttons.length > 0 && messages.length > 0" class="preview">
          <div class="preview-label">Preview</div>
          <div class="preview-bubble">
            <div class="preview-message">
              {{ messages[messages.length - 1]?.content || 'Message' }}
            </div>
            <div class="preview-buttons">
              <div v-for="(btn, idx) in buttons" :key="idx" class="preview-btn">
                {{ btn.label || 'Button ' + (idx + 1) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.greeting-editor {
  width: 100%;
}

.section {
  margin-bottom: 20px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.section-title {
  font-weight: 600;
  font-size: 14px;
  color: #374151;
}

.add-btn {
  padding: 4px 12px;
  font-size: 12px;
  color: #6366f1;
  border: 1px solid #6366f1;
  border-radius: 4px;
  background: var(--wlab-bg-primary);
  cursor: pointer;
}

.add-btn:hover {
  background: #6366f1;
  color: var(--wlab-text-primary);
}

.empty-hint {
  color: #9ca3af;
  font-size: 13px;
  font-style: italic;
}

.message-item {
  margin-bottom: 12px;
  padding: 12px;
  background: var(--wlab-bg-primary);
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.message-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.message-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.message-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.delay-input {
  width: 50px;
  padding: 2px 6px;
  font-size: 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
}

.delay-label {
  font-size: 11px;
  color: #9ca3af;
}

.delete-btn {
  padding: 2px 8px;
  font-size: 12px;
  color: #ef4444;
  background: none;
  border: none;
  cursor: pointer;
}

.delete-btn:hover {
  color: #dc2626;
}

.message-textarea {
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  resize: none;
}

.message-textarea:focus {
  outline: none;
  border-color: #6366f1;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.toggle-text {
  font-weight: 600;
  font-size: 14px;
  color: #374151;
}

.buttons-section {
  margin-top: 12px;
}

.button-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.button-input {
  flex: 1;
  padding: 6px 10px;
  font-size: 13px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
}

.button-input:focus {
  outline: none;
  border-color: #6366f1;
}

.arrow {
  color: #9ca3af;
  font-size: 12px;
}

.preview {
  margin-top: 16px;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 8px;
}

.preview-label {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 8px;
}

.preview-bubble {
  max-width: 280px;
  padding: 12px;
  background: var(--wlab-bg-primary);
  border-radius: 12px;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.preview-message {
  font-size: 13px;
  color: #374151;
  margin-bottom: 10px;
  line-height: 1.4;
  white-space: pre-wrap;
}

.preview-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
}

.preview-btn {
  padding: 8px 12px;
  text-align: center;
  font-size: 13px;
  color: #6366f1;
  background: var(--wlab-bg-primary);
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
</style>
